import React from 'react';
import { find, filter, get, isEmpty, cloneDeep, isArray, debounce, includes, toLower, size, findIndex, isUndefined, forEach } from 'lodash';

// local dependencies
import { accessManagementUserGroups as AccessManagementUserGroupsView } from 'ui/views/widgets/accessManagementUserGroups';
import { AccessManagementUserGroupsService } from 'services/accessManagementUserGroups';
import { SERVICE_RESPONSE_STATUS, ACCESS_MANAGEMENT_USER_GROUPS_MODE, CREATE_USER_GROUP_TABS, SIZES } from 'constants/common';
import * as errorOverlay from 'utils/errorOverlay';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import * as successOverlay from 'utils/successOverlay';
import { overlay } from 'modules/overlay';
import { OVERLAY_VIEWS } from 'constants/templates';
import { getLabel } from 'utils/getLabel';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { AccessManagementUserGroups } from 'ui/containers/AccessManagementUserGroups';
 *
 * // render
 * <AccessManagementUserGroups propName='propValue'/>
 */
export class AccessManagementUserGroups extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        this.initialState = {
            isFetching: true,
            networkStatus: undefined,
            componentData: {},

            mode: ACCESS_MANAGEMENT_USER_GROUPS_MODE.LIST,
            selectedTab: null,

            isSearchingUserGroup: false,
            userGroupSearchQuery: '',
            userGroupSearchResults: [],

            formData: {},
            selectedPermissions: [],

            isSearchingUsers: false,
            usersSearchQuery: '',
            userSearchResults: [],
            addedUsers: [],

            errorMessage: null
        };

        this.state = cloneDeep( this.initialState );

        this.accessManagementUserGroupsService = new AccessManagementUserGroupsService();

        this.fetchComponentData();
    }

    /**
     * Fetch widget data
     */
    fetchComponentData = async () => {
        log.debug( 'AccessManagementUserGroups.fetchComponentData()' );

        this.accessManagementUserGroupsService.cancel();

        const { url } = this.props.widget;

        try {
            const { status, data } = await this.accessManagementUserGroupsService.fetchComponentData( { url } );

            if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
                this.setState( {
                    isFetching: false,
                    networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                    componentData: data,
                    selectedTab: find( data.createUserGroupSection.tabs, { config: { selected: true } } ).name,
                    selectedPermissions: [ ...data.options.permissionsOptions ],
                    userGroupSearchResults: [],
                    isSearchingUserGroup: false,
                    userGroupSearchQuery: ''
                } );
            } else {
                this.setState( {
                    isFetching: false,
                    networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
                } );
            }
        } catch ( error ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        }
    }

    /**
     * Handle user group search
     */
    handleUserGroupSearch = debounce( ( { values } ) => {
        log.debug( 'AccessManagementUserGroups.handleUserSearch()' );

        const searchQuery = values.userGroupSearch;

        if ( isEmpty( searchQuery ) ) {
            this.setState( {
                isSearchingUserGroup: false,
                userGroupSearchQuery: '',
                userGroupSearchResults: []
            } );

            return;
        }

        this.setState( {
            isSearchingUserGroup: true,
            userGroupSearchQuery: searchQuery,
            userGroupSearchResults: filter( this.state.componentData.userGroups, userGroup => includes( toLower( userGroup.name ), toLower( searchQuery ) ) )
        } );
    }, 300 );

    /**
     * Handle Create User Group action click
     */
    handleCreateUserGroupActionClick = () => {
        log.debug( 'AccessManagementUserGroups.handleCreateUserGroupActionClick()' );

        this.setState( { mode: ACCESS_MANAGEMENT_USER_GROUPS_MODE.CREATE } );
    }

    /**
     * Handle Cancel action click
     */
    handleCancelActionClick = () => {
        log.debug( 'AccessManagementUserGroups.handleCancelActionClick()' );

        this.setState( {
            mode: ACCESS_MANAGEMENT_USER_GROUPS_MODE.LIST,
            selectedTab: CREATE_USER_GROUP_TABS.USER_GROUP_TAB,

            isSearchingUserGroup: false,
            userGroupSearchQuery: '',
            userGroupSearchResults: [],

            formData: {},
            selectedPermissions: [ ...this.state.componentData.options.permissionsOptions ],

            isSearchingUsers: false,
            addedUsers: [],
            userSearchResults: [],
            
            errorMessage: null
        } );
    }

    /**
     * Handle form (name and description field) data change
     * @param {object} param0 Object containing form values
     */
    handleFormDataChange = ( { values } ) => {
        log.debug( 'AccessManagementUserGroups.handleFormDataChange()' );

        this.setState( { formData: { ...values, id: get( this.state.formData, 'id', undefined ) } } );
    }

    /**
     * Handle permission change
     * @param {string} selectedPermission
     */
    handlePermissionSelect = ( selectedPermission ) => {
        log.debug( 'AccessManagementUserGroups.handlePermissionSelect()' );
        
        const selectedPermissions = [ ...this.state.selectedPermissions ];

        this.setState( {
            selectedPermissions: find( selectedPermissions, { id: selectedPermission.id } ) ?
                                    filter( selectedPermissions, permission => permission.id !== selectedPermission.id ) :
                                    [ ...selectedPermissions, selectedPermission ]
        } );
    }

    /**
     * Check validity of user group name and handle going to next step
     * @param {object} selectedTabObj Object containing selected tab info
     */
    handleGoToUsersActionClick = async ( selectedTabObj ) => {
        log.debug( 'AccessManagementUserGroups.handleGoToUsersActionClick()' );

        this.accessManagementUserGroupsService.cancel();

        pageLevelPreloader.show();

        const goToUsersAction = find( selectedTabObj.actions, { name: 'goToUsers' } );
        const url = get( goToUsersAction, 'userGroupNameValidation.url', {} );

        const isEditMode = ACCESS_MANAGEMENT_USER_GROUPS_MODE.EDIT === get( this.state, 'mode', '' );

        const { status, data } = await this.accessManagementUserGroupsService.validateNewUserGroupName( {
            url,
            data: { data: {
                id: isEditMode ? get( this.state, 'formData.id', undefined ): undefined,
                searchQuery: this.state.formData.userGroupName } }
        } );

        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            if ( data.isUserGroupNameValid ) {

                if( isEditMode ) {

                    const _url = get( goToUsersAction, 'fetchEditUserGroupMembers.url', {} );
                    const groupMemberList = await this.accessManagementUserGroupsService.fetchGroupMemberList( {
                        url: _url,
                        data: {
                            id: get( this.state, 'formData.id', '' ) }
                    } );

                    if( SERVICE_RESPONSE_STATUS.SUCCESS === groupMemberList.status ) {

                        this.setState( {
                            selectedTab: CREATE_USER_GROUP_TABS.USERS_TAB,
                            errorMessage: null,
                            addedUsers: groupMemberList.data
                        }, () => pageLevelPreloader.hide() );
                    } else {
                        pageLevelPreloader.hide();
                        errorOverlay.open();
                    }
                } else {

                    this.setState( {
                        selectedTab: CREATE_USER_GROUP_TABS.USERS_TAB,
                        errorMessage: null
                    }, () => pageLevelPreloader.hide() );
                }
            } else {
                this.setState( {
                    errorMessage: get( data, 'description', '' )
                }, () =>  {
                    pageLevelPreloader.hide();
                    window.scrollTo( 0, 0 );
                } );
            }
        } else {
            pageLevelPreloader.hide();
            errorOverlay.open();
        }
    }

    /**
     * Handle user search
     */
    handleUserSearch = debounce( ( { values, field } ) => {
        log.debug( 'AccessManagementUserGroups.handleUserSearch()' );

        const searchQuery = values.usersSearch;
        const matchingAddedUsers = [];
        const nonMatchingAddedUsers = [];

        forEach( this.state.addedUsers, addedUser => {
            if( includes( toLower( addedUser.name ), toLower( searchQuery ) ) || includes( toLower( addedUser.email ), toLower( searchQuery ) ) ) {
                matchingAddedUsers.push( addedUser );
            } else {
                nonMatchingAddedUsers.push( addedUser );
            }
        } );

        this.setState( {
            isSearchingUsers: 3 <= size( searchQuery ),
            usersSearchQuery: searchQuery,
            addedUsers: [ ...matchingAddedUsers, ...nonMatchingAddedUsers ]
        }, async () => {
            if ( ! this.state.isSearchingUsers ) {
                this.setState( { userSearchResults: [] } );
                return;
            }

            const { status, data: responseData } = await this.accessManagementUserGroupsService.fetchUserSearchResults( {
                url: field.config.searchUrl,
                data: { data: { searchQuery } }
            } );

            if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
                const { users } = responseData;

                this.setState( {
                    isSearchingUsers: false,
                    userSearchResults: filter( users, user => -1 === findIndex( this.state.addedUsers, { id: user.id } ) )
                } );
            } else {
                this.setState( {

                    // isSearchingUsers: false,
                    userSearchResults: []
                } );
            }
        } );
    }, 500 );

    /**
     * Handle adding user
     * @param {object} user Object of user to be added
     */
    handleUserAdd = ( user ) => {
        log.debug( 'AccessManagementUserGroups.handleUserAdd()' );

        this.setState( {
            userSearchResults: filter( this.state.userSearchResults, result => result.id !== user.id ),
            addedUsers: [ user, ...this.state.addedUsers,  ]
        } );
    }

    /**
     * Handle removing user
     * @param {object} user Object of user to be removed
     */
    handleUserRemove = ( user ) => {
        log.debug( 'AccessManagementUserGroups.handleUserRemove()' );

        this.setState( {
            addedUsers: filter( this.state.addedUsers, addedUser => addedUser.id !== user.id )
        } );
    }

    /**
     * Handle going back to information page
     */
    handleGoToInformationActionClick = () => {
        log.debug( 'AccessManagementUserGroups.handleGoToInformationActionClick()' );
        
        this.setState( {
            selectedTab: CREATE_USER_GROUP_TABS.USER_GROUP_TAB,
            userSearchResults: []
        } );
    }

    /**
     * Handle submission of new user group data
     * @param {object} selectedTabObj Object containing information of selected tab
     */
    handleSubmitFormData = async ( selectedTabObj ) => {
        log.debug( 'AccessManagementUserGroups.handleSubmitFormData()' );

        this.accessManagementUserGroupsService.cancel();

        pageLevelPreloader.show();

        const url = find( selectedTabObj.actions, { name: 'submit' } ).url;
        const isEditMode = ACCESS_MANAGEMENT_USER_GROUPS_MODE.EDIT === get( this.state, 'mode', '' );

        const { status, data } = await this.accessManagementUserGroupsService.submitFormData( {
            url,
            data: {
                data: {
                    name: this.state.formData.userGroupName,
                    description: this.state.formData.userGroupDescription,
                    permissions: this.state.selectedPermissions,
                    users: this.state.addedUsers,
                    id: isEditMode ? get( this.state, 'formData.id', undefined ): undefined
                }
            }
        } );

        pageLevelPreloader.hide();

        if ( SERVICE_RESPONSE_STATUS.SUCCESS === status ) {
            successOverlay.open( {
                message: get( data, 'title', {} ),
                config: {
                    autoClose: false,
                    overlaySize: SIZES.LARGE,
                    messageSize: SIZES.LARGE,
                    onCloseCallback: () => this.setState( cloneDeep( this.initialState ), this.fetchComponentData )
                }
            } );
        } else {
            errorOverlay.open();
        }
    }

    /**
     *
     * @param {*} payload: has action config + userGroups listing card object
     */
    handleListingCardActions = ( payload ) => {
        log.debug( 'AccessManagementUserGroups.handleListingCardActions()' );

        if( 'deleteUserGroup' === payload.name ) {
            this.handleDeleteGroup( payload ); // delete a user group card action
        } else if( 'editUserGroup' === payload.name ) {
            this.setState( {
                mode: ACCESS_MANAGEMENT_USER_GROUPS_MODE.EDIT,
                selectedPermissions: get( payload, 'userGroup.permissions', [] ),
                formData: {
                    userGroupName: get( payload, 'userGroup.name', '' ),
                    userGroupDescription: get( payload, 'userGroup.description', '' ),
                    id: get( payload, 'userGroup.id', '' )
                },
                addedUsers: get( payload, 'userGroup.users', [] )
            } );
        }
    }


    /***
     * handle delete action click
     */
    handleDeleteGroup = ( payload ) => {
        log.debug( 'AccessManagementUserGroups.handleDeleteGroup()' );

        const currentUserGroups = cloneDeep( get( this.state, 'componentData.userGroups', [] ) );

        if( isEmpty( currentUserGroups ) || !isArray( currentUserGroups ) ) {
            return null;
        }

        overlay.open( {
            settings: {
                modalType: OVERLAY_VIEWS.CONFIRMATION_PROMPT_WITH_ICON,
                showCloseButton: true,
                ignoreOutsideClick: true,
            },
            config: {},
            data: {
                message: get( payload, 'prompt.title', {} ),
                subMessage: getLabel( get( payload, 'prompt.description', {} ) ).replace( '${userGroupName}', get( payload.userGroup, 'name', '' ) ),
                actions: get( payload, 'prompt.actions', [] ),
                onConfirmClick: ( action ) => {
                    this.submitDeleteGroup( { ...action, userGroup: payload.userGroup }, currentUserGroups );
                    overlay.close();
                }
            },
        } );

    }

    /**
     *  handles delete user group from BE
     * @param {*} payload
     * @param {*} currentUserGroups
     * @returns
     */
    submitDeleteGroup = async( payload ) => {
        log.debug( 'AccessManagementUserGroups.submitDeleteGroup()' );

        const url = get( payload, 'url', '' );

        const groupId = get( payload, 'userGroup.id', undefined );

        if( isEmpty( url ) || isUndefined( groupId ) ) {
            
            return errorOverlay.open();
        }

        const requestData = {
            id: groupId
        };

        try {
            const { status, data } = await this.accessManagementUserGroupsService.deleteUserGroupData( { url, data: requestData } );

            // check if response status was success
            if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {

                if( !isEmpty( data ) ) {
                    errorOverlay.open( {  size: SIZES.SMALL, message: data.description } );
                } else {
                    errorOverlay.open();
                }
            } else {
                successOverlay.open( {
                    message: getLabel( get( data, 'title', {} ) ),
                    config: {
                        autoClose: false,
                        overlaySize: SIZES.LARGE,
                        messageSize: SIZES.LARGE,
                        onCloseCallback: () => { this.setState( { isFetching: true, userGroupSearchResults: [], isSearchingUserGroup: false, userGroupSearchQuery: '' }, () => { this.fetchComponentData(); } ); }
                    }
                } );
            }
        } catch ( error ) {
            errorOverlay.open();
        }

    }

    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <AccessManagementUserGroupsView
                { ...this.state }
                onCreateUserGroupActionClick={ this.handleCreateUserGroupActionClick }
                onCancelActionClick={ this.handleCancelActionClick }
                onGoToUsersActionClick={ this.handleGoToUsersActionClick }
                onPermissionSelect={ this.handlePermissionSelect }
                handleListingCardActions = { this.handleListingCardActions }
                onUserSearch={ this.handleUserSearch }
                onUserAdd={ this.handleUserAdd }
                onUserRemove={ this.handleUserRemove }
                onGoToInformationActionClick={ this.handleGoToInformationActionClick }
                onFormDataChange={ this.handleFormDataChange }
                onSubmitFormData={ this.handleSubmitFormData }
                onUserGroupSearch={ this.handleUserGroupSearch }
            />
        );
    }
}

// set display name
AccessManagementUserGroups.displayName = 'AccessManagementUserGroups';

// set default props
AccessManagementUserGroups.defaultProps = {};

// prop types
AccessManagementUserGroups.propTypes = {};
